import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#005DE8] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Mobikoo</span>
          </Link>
          <Link to="/" className="text-sm text-[#005DE8] hover:underline flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-8">
          <p className="text-sm text-[#005DE8] font-medium mb-2">Legal</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit' }}>Privacy Policy</h1>
          <p className="text-[#64748B]">How we collect, use, and protect your information</p>
          <p className="text-sm text-[#94A3B8] mt-1">Version 1.10</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>1. Introduction</h2>
            <p className="text-[#475569] leading-relaxed mt-3">Welcome to Mobikoo. This Privacy Policy explains how Mobikoo ("we", "our", or "us") collects, uses, stores, and protects information about you when you use our website, mobile application, and extended warranty services (collectively, the "Platform").</p>
            <p className="text-[#475569] leading-relaxed">By using our Platform, you consent to the practices described in this Privacy Policy. If you do not agree, please discontinue use of our Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>2. Who We Are</h2>
            <p className="text-[#475569] leading-relaxed mt-3">Mobikoo is an extended warranty service provider for new mobile phones in India. We are not an insurance company and do not provide insurance products. Our services are limited to extended warranty coverage for manufacturing defects on new mobile handsets, as defined in our Terms and Conditions.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>3. Information We Collect</h2>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">3.1 Information from End Customers</h3>
            <p className="text-[#475569] leading-relaxed">When a partner shop registers a warranty on your behalf, we collect:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>Full name</li>
              <li>Mobile phone number</li>
              <li>Email address (if provided)</li>
              <li>City and pin code</li>
              <li>Device details: brand, model, IMEI number</li>
              <li>Date of purchase and purchase price</li>
              <li>Warranty plan selected and duration</li>
            </ul>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">3.2 Information from Partner Shops</h3>
            <p className="text-[#475569] leading-relaxed">When a shop owner registers as a Mobikoo partner, we collect:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>Shop owner name and contact number</li>
              <li>Shop name and business address</li>
              <li>City and pin code</li>
              <li>Bank account details for commission payments</li>
              <li>Government-issued ID for KYC verification</li>
            </ul>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">3.3 Technical Information</h3>
            <p className="text-[#475569] leading-relaxed">We automatically collect certain technical data when you access our Platform:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>IP address and browser type</li>
              <li>Device type and operating system</li>
              <li>Pages visited and time spent on the Platform</li>
              <li>Referring URLs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>4. How We Use Your Information</h2>
            <p className="text-[#475569] leading-relaxed mt-3">We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>To register and manage extended warranty plans</li>
              <li>To send warranty confirmation messages via WhatsApp and/or SMS</li>
              <li>To process warranty service requests and claims</li>
              <li>To process partner commission payments</li>
              <li>To communicate important updates about your warranty or account</li>
              <li>To verify identity and prevent fraud</li>
              <li>To improve our Platform and services</li>
              <li>To comply with applicable legal obligations</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mt-3">We do not use your information for unsolicited marketing communications without your prior consent.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>5. How We Share Your Information</h2>
            <p className="text-[#475569] leading-relaxed mt-3">We do not sell your personal data. We may share your information only in the following circumstances:</p>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">5.1 Service Providers</h3>
            <p className="text-[#475569] leading-relaxed">We share data with trusted third-party vendors who help us operate our Platform, including cloud hosting providers, payment processors, and messaging service providers (e.g., WhatsApp Business API providers). These vendors are contractually bound to protect your data and may not use it for any other purpose.</p>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">5.2 Warranty Service Fulfilment</h3>
            <p className="text-[#475569] leading-relaxed">When you raise a warranty service request, we may share relevant device and contact information with authorised service centres to facilitate repair or replacement.</p>

            <h3 className="text-base font-semibold text-[#1E293B] mt-4">5.3 Legal Requirements</h3>
            <p className="text-[#475569] leading-relaxed">We may disclose your information if required to do so by law, court order, or government authority, or if we believe disclosure is necessary to protect our rights or the safety of any person.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>6. Data Storage and Security</h2>
            <p className="text-[#475569] leading-relaxed mt-3">Your data is stored on secure servers hosted in India. We implement industry-standard technical and organisational measures to protect your personal information from unauthorised access, alteration, disclosure, or destruction, including:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>SSL/TLS encryption for all data in transit</li>
              <li>Access controls limiting data access to authorised personnel only</li>
              <li>Regular security audits and vulnerability assessments</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mt-3">While we take every reasonable precaution, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your data.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>7. Data Retention</h2>
            <p className="text-[#475569] leading-relaxed mt-3">We retain your personal data for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. Specifically:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li>Warranty registration data is retained for the duration of the warranty plan plus 2 years</li>
              <li>Partner account data is retained for the duration of the partnership plus 3 years</li>
              <li>Technical logs are retained for up to 12 months</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mt-3">After the retention period, your data will be securely deleted or anonymised.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>8. Your Rights</h2>
            <p className="text-[#475569] leading-relaxed mt-3">As a user of our Platform, you have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 text-[#475569] space-y-1">
              <li><strong>Right to access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Right to correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to deletion:</strong> Request deletion of your data, subject to legal obligations</li>
              <li><strong>Right to withdraw consent:</strong> Withdraw consent for processing where consent is the legal basis</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mt-3">To exercise any of these rights, please contact us at <a href="mailto:privacy@mobikoo.com" className="text-[#005DE8] hover:underline">privacy@mobikoo.com</a>. We will respond to your request within 30 days.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>9. Cookies and Tracking</h2>
            <p className="text-[#475569] leading-relaxed mt-3">Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyse traffic, and understand user behaviour. You can control cookie settings through your browser. Disabling cookies may affect some features of our Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>10. Third-Party Links</h2>
            <p className="text-[#475569] leading-relaxed mt-3">Our Platform may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites and encourage you to read their privacy policies separately.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>11. Changes to This Policy</h2>
            <p className="text-[#475569] leading-relaxed mt-3">We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on our Platform with a revised effective date. Continued use of our Platform after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0F172A] border-b border-slate-100 pb-2" style={{ fontFamily: 'Outfit' }}>12. Contact Us</h2>
            <p className="text-[#475569] leading-relaxed mt-3">If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us. All the contact info is available on the <Link to="/" className="text-[#005DE8] hover:underline">contact us page</Link> of the website.</p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-[#64748B]">
          &copy; {new Date().getFullYear()} Mobikoo. All rights reserved. | <Link to="/terms" className="hover:text-[#005DE8]">Terms & Conditions</Link> | <Link to="/privacy" className="hover:text-[#005DE8]">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}
